import {
  Button,
  InformationCard,
  SafeHtmlContent
} from '@yes.technology/react-toolkit'
import useIsOpen from 'modules/shared/hooks/useIsOpen'
import { useCallback, useEffect } from 'react'
import { useAppDispatch, useAppSelector } from 'state/store'
import { Step, InformationCard as TInformationCard } from 'types/shared'
import { interactionModelOperations } from '../redux'
import { useTranslation } from 'react-i18n-lite'
import useInterpolateUrl from '../hooks/useInterpolateUrl'

type GetCurrentCardParams = {
  cards: Record<string, TInformationCard[]>
  currentStepUuid?: string
  firstStepUuid: string | undefined
  currentStep: Step
  isInteractionFinished: boolean
  dismissedInformationCards: string[]
}

const getCurrentCard = ({
  cards,
  currentStep,
  currentStepUuid,
  firstStepUuid,
  isInteractionFinished,
  dismissedInformationCards
}: GetCurrentCardParams) => {
  if (isInteractionFinished) {
    return cards['interaction-finished']?.[0]
  }

  if (
    currentStepUuid === firstStepUuid &&
    !dismissedInformationCards?.includes('interaction-started')
  ) {
    return cards['interaction-started']?.[0]
  }

  if (!dismissedInformationCards?.includes(currentStep?.uuid)) {
    return cards['step-started']?.find(
      (card) => card.step_uuid === currentStep?.uuid
    )
  }
}

type InteractionInformationProps = {
  cards: TInformationCard[]
  currentStep: Step
  currentStepUuid?: string
  isInteractionFinished: boolean
}

export default function InteractionInformation({
  cards = [],
  currentStep,
  currentStepUuid,
  isInteractionFinished
}: InteractionInformationProps) {
  const dispatch = useAppDispatch()

  const { t } = useTranslation()

  const interpolateUrl = useInterpolateUrl()

  const interactionModel = useAppSelector((state) => state.interactionModel)
  const firstStepUuid = useAppSelector(
    (state) => state.steps.displayedSteps[0]?.uuid
  )

  const { isOpen, open, close } = useIsOpen(false)

  const flattenCards = cards.reduce(
    (acc, card) => {
      if (!acc[card.display_on]) {
        acc[card.display_on] = []
      }
      acc[card.display_on].push(card)
      return acc
    },
    {} as Record<string, TInformationCard[]>
  )

  const currentCard = getCurrentCard({
    cards: flattenCards,
    currentStep,
    currentStepUuid,
    firstStepUuid,
    isInteractionFinished,
    dismissedInformationCards: interactionModel.dismissedInformationCards
  })

  useEffect(() => {
    if (currentCard) {
      open()
    }
  }, [currentCard, open])

  const handleClose = useCallback(() => {
    if (currentCard?.display_on === 'interaction-started') {
      dispatch(
        interactionModelOperations.dismissInformationCard('interaction-started')
      )
    }

    if (currentCard?.display_on === 'step-started') {
      dispatch(
        interactionModelOperations.dismissInformationCard(currentStep.uuid)
      )
    }

    close()
  }, [currentCard, dispatch, close, currentStep?.uuid])

  if (!currentCard) {
    return null
  }

  const getActionButton = () => {
    const defaultLabel =
      currentCard.display_on === 'interaction-finished'
        ? t('step.information_card.button.close')
        : t('step.information_card.button.continue')
    const label = currentCard.action_button?.des || defaultLabel

    if (currentCard.action_button?.url) {
      return (
        <a
          href={interpolateUrl(currentCard.action_button?.url)}
          className='col-12'
        >
          <Button>{label}</Button>
        </a>
      )
    }

    return <Button onClick={handleClose}>{label}</Button>
  }

  return (
    <InformationCard
      title={currentCard.des}
      spacingClass='small'
      close={handleClose}
      style={{ padding: '40px' }}
      {...{ isOpen }}
    >
      <div className='d-flex flex-column gap-4 align-items-center mt-1 p-3'>
        {currentCard.image_url && (
          <img
            src={currentCard.image_url}
            alt=''
            className='rounded-circle'
            style={{
              border: '2px solid #DDE5ED',
              maxWidth: 280,
              aspectRatio: 1 / 1,
              objectFit: 'cover'
            }}
          />
        )}
        <span style={{ fontSize: '18px', lineHeight: '24px' }}>
          <SafeHtmlContent html={currentCard.complement} />
        </span>
        {getActionButton()}
      </div>
    </InformationCard>
  )
}
